import { Suspense, lazy, useEffect, useState } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useTemporarySidebarCollapse } from '@/components/layout/sidebarCollapseRequest';
import { MapStyleToggle } from '@/components/map/MapStyleToggle';
import { Skeleton } from '@/components/ui/skeleton';
import { MoJourneyOverlay } from '../components/MoJourneyOverlay';
import { MoMobileJourneyPanel } from '../components/MoMobileJourneyPanel';
import { MoRouteMapBoundary } from '../components/MoRouteMapBoundary';
import { MoRouteSummaryCards } from '../components/MoRouteSummaryCards';
import { MoTravelDetailHeader } from '../components/MoTravelDetailHeader';
import { MoTravelDetailPanel } from '../components/MoTravelDetailPanel';
import type { MoTravelMapSelection } from '../components/MoTravelMap';
import { useMoTravelDetail } from '../hooks/useMoTravelDetail';
import { useLastViewedMoStore } from '../store/lastViewedMo.store';

const MoTravelMap = lazy(() =>
  import('../components/MoTravelMap').then((module) => ({ default: module.MoTravelMap })),
);

interface MoTravelDetailPageProps {
  userId: number;
}

export function MoTravelDetailPage({ userId }: MoTravelDetailPageProps) {
  const router = useRouter();
  const navigate = useNavigate();
  const [isSatellite, setIsSatellite] = useState(false);
  const setLastViewedMoId = useLastViewedMoStore((state) => state.setLastViewedMoId);
  const {
    period,
    selectedDay,
    handlePeriodChange,
    handleDaySelect,
    handleDayInPeriodPick,
    route,
    routeQuery,
    dayRoutes,
    displayedRoutes,
    periodLabel,
    widerPeriodAction,
    journeyOverlay,
    journeyEntries,
    startPlaces,
    canReturnToStartPicker,
    hoveredKey,
    focusedKey,
    flyToRequest,
    mapSectionRef,
    journeyPanelRef,
    focusedEntry,
    focusedStopDetail,
    canStepJourney,
    isStackedLayout,
    statsDate,
    handleTimelineStopSelect,
    handleShowWholePeriod,
    handleWaypointHover,
    handleWaypointFocus,
    handleStopFocusClear,
    handleJourneyStart,
    handleJourneyExit,
  } = useMoTravelDetail(userId);
  useTemporarySidebarCollapse(userId);

  useEffect(() => {
    setLastViewedMoId(userId);
  }, [userId, setLastViewedMoId]);

  function handleBack() {
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }
    void navigate({ to: '/geo-location/mo-travel-history', search: period });
  }

  const mapSelection: MoTravelMapSelection = {
    moId: userId,
    homeDivision: route?.profile.division ?? null,
    routes: displayedRoutes,
    isLoading: routeQuery.isLoading,
    hasError: routeQuery.isError,
    selectedDay,
    periodLabel,
    highlightedKey: hoveredKey,
    focusedKey,
    hasDetailsBelowMap: isStackedLayout,
    flyToRequest,
    onWaypointHover: handleWaypointHover,
    onWaypointClick: handleWaypointFocus,
    onClearFocus: handleStopFocusClear,
  };

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <MoTravelDetailHeader
        personName={route?.profile.fullName}
        isPersonLoading={routeQuery.isLoading}
        period={period}
        journey={{
          routes: dayRoutes,
          selectedDay,
          isLoading: routeQuery.isLoading,
          onDayInPeriodPick: handleDayInPeriodPick,
          onDayClear: () => handleDaySelect(null),
        }}
        statsDate={statsDate}
        onPeriodChange={handlePeriodChange}
        onShowWholePeriod={handleShowWholePeriod}
        onBack={handleBack}
      />

      <MoRouteSummaryCards
        route={route}
        mapOn={period.mapOn}
        statsDate={statsDate}
        isLoading={routeQuery.isLoading}
        isError={routeQuery.isError}
      />

      <div className="flex min-h-0 flex-1 flex-col rounded-xl border bg-card max-lg:overflow-clip lg:flex-row lg:overflow-hidden">
        <div
          ref={mapSectionRef}
          className="relative isolate h-[45svh] min-h-72 min-w-0 scroll-mt-36 bg-muted lg:h-auto lg:min-h-80 lg:flex-1"
        >
          <MoRouteMapBoundary>
            <Suspense fallback={<Skeleton className="size-full rounded-none" />}>
              <MoTravelMap isSatellite={isSatellite} selection={mapSelection} />
            </Suspense>
          </MoRouteMapBoundary>
          <MapStyleToggle
            isSatellite={isSatellite}
            onChange={setIsSatellite}
            showsOnlyOtherStyle
            className="absolute top-3 right-3 z-10"
          />
          {!isStackedLayout && (
            <MoJourneyOverlay
              overlay={journeyOverlay}
              journeyEntries={journeyEntries}
              startPlaces={startPlaces}
              focusedKey={focusedKey}
              canReturnToStartPicker={canReturnToStartPicker}
              onStepSelect={handleWaypointFocus}
              onStartHover={handleWaypointHover}
              onJourneyStart={handleJourneyStart}
              onJourneyExit={handleJourneyExit}
            />
          )}
        </div>

        {isStackedLayout && (
          <MoMobileJourneyPanel
            ref={journeyPanelRef}
            overlay={journeyOverlay}
            journeyEntries={journeyEntries}
            startPlaces={startPlaces}
            focusedEntry={focusedEntry}
            focusedStopDetail={focusedStopDetail}
            canStepJourney={canStepJourney}
            canReturnToStartPicker={canReturnToStartPicker}
            onStepSelect={handleWaypointFocus}
            onJourneyStart={handleJourneyStart}
            onJourneyExit={handleJourneyExit}
            onFocusClear={handleStopFocusClear}
          />
        )}

        <div className="flex shrink-0 flex-col border-t lg:w-[30%] lg:min-w-72 lg:overflow-hidden lg:border-t-0 lg:border-l">
          <MoTravelDetailPanel
            route={route}
            isLoading={routeQuery.isLoading}
            error={routeQuery.error}
            onRetry={() => void routeQuery.refetch()}
            timelineRoutes={displayedRoutes}
            isDayFiltered={selectedDay !== null}
            statsDate={statsDate}
            hoveredKey={hoveredKey}
            focusedKey={focusedKey}
            onStopHover={handleWaypointHover}
            onStopSelect={handleTimelineStopSelect}
            periodLabel={periodLabel}
            widerPeriodAction={widerPeriodAction}
          />
        </div>
      </div>
    </div>
  );
}
